import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@repo/ui/src/components/ui/button';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { DatePicker } from '../../components/pickers/DatePicker';
import { useUpdateClass } from '../../hooks/useClasses';
import { todayHouseIso } from '../../lib/houseToday';

/**
 * THI-267 Task 12 — ClassDetailPage's ⋮ → "Kết thúc lớp" (spec §5,
 * S5's own consequence copy): writes `ClassGroup.endsOn` via
 * `PUT /classes/:id`. A small centered `Dialog`, not `ResponsiveSheet` — one
 * date field and one consequence sentence, the same weight as
 * `TemplateDialogs`' own small forms (INVARIANTS §6.4 reserves
 * `ResponsiveSheet` for real multi-field panels, not this).
 */
export function EndClassDialog({
  open, onOpenChange, classId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  classId: string;
}) {
  const { t } = useTranslation('scheduling');
  const update = useUpdateClass(classId);
  const [endsOn, setEndsOn] = useState(todayHouseIso());

  // Reset-on-open, same "adjust state during render" technique ResponsiveSheet
  // and ClassFormSheet use — the dialog stays mounted between opens (owned by
  // ClassDetailPage), so a plain useState initializer would only run once.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setEndsOn(todayHouseIso());
  }

  function handleConfirm() {
    update.mutate(
      { endsOn },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err: Error) => toast.error(err.message || t('classDetail.endDialog.error')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('classDetail.endDialog.title')}</DialogTitle>
          <DialogDescription>{t('classDetail.endDialog.consequence')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-1">
          <Label>{t('classDetail.endDialog.dateLabel')}</Label>
          <DatePicker value={endsOn} onChange={setEndsOn} className="w-full justify-between font-normal" />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            {t('classDetail.endDialog.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={update.isPending || !endsOn}>
            {t('classDetail.endDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
